import type { FC } from "react";

import { useTranslation } from "react-i18next";

import type { OccupantFormValues } from "../hooks/use-occupant-form-schema";

import FormInputField from "./form-input-field";
import OccupantDestinationFields from "./occupant-destination-fields";

interface OccupantContactFieldsProps {
  /** The client's own primary or billing contact: its name, numbers and
   * addresses are the account information, edited on its own step — here only
   * its alert boxes can change. */
  lockDetails?: boolean;
}

/**
 * The whole person: their name and every destination they can be alerted at.
 * The one face of the drawer — the same fields whether the contact is being
 * created or an existing one is being edited.
 */
const OccupantContactFields: FC<OccupantContactFieldsProps> = ({ lockDetails = false }) => {
  const { t } = useTranslation(["occupants"]);

  return (
    <div className="flex flex-col gap-6">
      {lockDetails && (
        <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
          {t(
            "occupants:occupants-account-contact-locked",
            "This is the account holder: their name, numbers and email addresses are changed in your account information. Only their alerts are set here.",
          )}
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 flex-col gap-2">
          <span className="text-sm font-medium leading-none text-foreground">
            {t("occupants:occupants-field-first-name", "First name")}
          </span>
          <FormInputField<OccupantFormValues>
            disabled={lockDetails}
            name="first_name"
            placeholder={t("occupants:occupants-field-first-name-placeholder", "First name")}
          />
        </div>

        <div className="flex flex-1 flex-col gap-2">
          <span className="text-sm font-medium leading-none text-foreground">
            {t("occupants:occupants-field-last-name", "Last name")}
          </span>
          <FormInputField<OccupantFormValues>
            disabled={lockDetails}
            name="last_name"
            placeholder={t("occupants:occupants-field-last-name-placeholder", "Last name")}
          />
        </div>
      </div>

      <OccupantDestinationFields lockDetails={lockDetails} />
    </div>
  );
};

export default OccupantContactFields;
